'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { isAfter, isSameDay, isSameMonth, startOfDay, format, getYear, getMonth } from 'date-fns';
import { MdChevronLeft, MdChevronRight, MdClose, MdDone, MdExpandMore } from 'react-icons/md';
import { useTranslations } from 'next-intl';
import { CircleLoader, CircleLoaderSize } from '@/components/ui/loaders/circle-loader/CircleLoader';
import { Colors } from '@/lib/colorUtils';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  isDateDisabled,
  buildCalendarGrid,
  chunk,
  WEEKDAY_KEYS,
  MONTH_KEYS,
  CALENDAR_FRAME_HEIGHT,
} from './calendar-utils';
import type { TimeValue } from './calendar-utils';
import { MonthGrid } from './MonthGrid';
import { YearGrid } from './YearGrid';
import { TimePanel } from './TimePanel';
import { useCalendarPopup } from './useCalendarPopup';
import {
  calendarContainerClasses,
  calendarWithTimeContainerClasses,
  calendarWithTimeBodyClasses,
  calendarHeaderClasses,
  calendarSelectorButtonClasses,
  calendarWeekHeaderClasses,
  calendarWeekHeaderCellClasses,
  calendarGridClasses,
  calendarDayCellClasses,
  calendarFooterClasses,
  calendarFooterNavGroupClasses,
  calendarFooterSpacerClasses,
  calendarNavButtonClasses,
  calendarDoneButtonClasses,
  calendarDesktopOverlayClasses,
  calendarDesktopDropdownClasses,
  calendarMobileBackdropClasses,
  calendarMobileDialogClasses,
  calendarMobileHeaderClasses,
  calendarMobileTitleClasses,
  calendarMobileCloseButtonClasses,
  calendarMobileBodyClasses,
} from './CalendarPanel.style';
import { ErrorBanner } from '@/components/ui/error-banner';

export interface CalendarPanelProps {
  open: boolean;
  onClose: () => void;
  /**
   * Surfaces that co-own this picker (e.g. the search row(s) the trigger sits in). A press there
   * dismisses the calendar and still reaches the control; every other outside press only
   * dismisses. See `useCalendarPopup` (PB-4947).
   */
  ownedSurfaceRefs?: ReadonlyArray<React.RefObject<HTMLElement | null>>;
  onSelect: (date: Date) => void;
  selectedDate?: Date | null;
  minDate?: Date;
  maxDate?: Date;
  filterDate?: (date: Date) => boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
  title?: string;
  /** Show only month and year selection (no day grid). */
  monthYearOnly?: boolean;
  /**
   * Show only the year grid (no day/month grid, no month footer). Selecting a year
   * calls `onSelect` with Jan 1 of that year and closes. Years outside
   * minDate/maxDate are disabled. Takes precedence over `monthYearOnly`; the time
   * panel is never shown in this mode.
   */
  yearOnly?: boolean;
  /** Enable the time panel alongside the calendar. */
  enableTime?: boolean;
  /** Current time value when time is enabled. */
  timeValue?: TimeValue | null;
  /**
   * Called when the user changes the time. Receives the panel's pending date so
   * the consumer applies the time to the day the user just clicked, not the last
   * committed value (which only updates on Done).
   */
  onTimeChange?: (time: TimeValue, pendingDate?: Date | null) => void;
  /** Interval between time slots in minutes. Default: 15. */
  timeInterval?: number;
  /** Return false to disable a specific time slot (greyed out, not clickable). */
  filterTime?: (time: TimeValue, date?: Date | null) => boolean;
  /** Override minimum width of the dropdown (default: 320, or 480 when enableTime). */
  baseMinWidth?: number;
  /** Render the Done (confirm) button in a loading state — spinner + disabled. */
  confirmLoading?: boolean;
  /**
   * Whether clicking Done closes the panel immediately. Default true. Set false
   * when the consumer closes asynchronously after a save completes (PB-1697) —
   * otherwise the panel would close before the in-flight save resolves.
   */
  closeOnConfirm?: boolean;
  /**
   * Optional inline error shown above the Done button — lets an async confirm
   * (e.g. a failed save when `closeOnConfirm` is false) surface the backend error
   * inside the panel while it stays open. Null/undefined renders nothing.
   */
  error?: string | null;
  /**
   * `data-cy` on the inline error banner. Defaults to `"calendar-error"`; a consumer passes its
   * own only when one kind of error needs a distinct selector (PB-7985's pay-first refusal uses
   * `"calendar-pay-first-message"`). The banner keeps `role="alert"` either way.
   */
  errorDataCy?: string;
  /**
   * Override the z-index of the portaled calendar when this panel is hosted inside a
   * high-stacking surface (a BottomSheet, or any self-portaling modal).
   *
   * ## Invariants:
   * - The calendar portals to `document.body` and defaults to z-50, so a calendar opened
   *   inside a sheet (backdrop 51 / panel 52) is positioned correctly and then painted over
   *   by its own host. To the parker that reads as "the calendar renders outside the dialog" —
   *   the part sticking out past the dialog's edges is all that stays visible (PB-5106). It is
   *   not a portal or overflow-clipping bug; do not chase it as one.
   * - Undefined leaves the default z-50 bands untouched, so every standalone DatePicker on a
   *   page keeps its current stacking. Only a host that needs to be cleared passes this.
   * - Pass `SHEET_MENU_Z_INDEX` from `@/lib/styleUtils` rather than a literal — that constant
   *   encodes the relationship to both the sheet bands below it and the toast/map/captcha
   *   layers above it, which must stay able to cover an open calendar.
   */
  menuZIndex?: number;
}

const MOBILE_QUERY = '(max-width: 767px)';

export function CalendarPanel({
  open,
  onClose,
  ownedSurfaceRefs,
  onSelect,
  selectedDate,
  minDate,
  maxDate,
  filterDate,
  anchorRef,
  title,
  monthYearOnly = false,
  yearOnly = false,
  enableTime: enableTimeProp = false,
  timeValue,
  onTimeChange,
  timeInterval,
  filterTime,
  baseMinWidth: baseMinWidthProp,
  confirmLoading = false,
  closeOnConfirm = true,
  error,
  errorDataCy = 'calendar-error',
  menuZIndex,
}: CalendarPanelProps) {
  // A year-only picker has no day to attach a time to.
  const enableTime = enableTimeProp && !yearOnly;
  const t = useTranslations('DatePicker');
  const tCommon = useTranslations('Common');
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const mobileDialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useFocusTrap(mobileDialogRef, open && isMobile);

  // The date the user has clicked but not yet confirmed.
  const [pendingDate, setPendingDate] = useState<Date | null>(() => selectedDate ?? null);

  // Figma spec (node 2563:2875): 371px calendar column, 217px time column → 588px combined.
  const baseMinWidth = baseMinWidthProp ?? (enableTime ? 588 : 371);

  const {
    viewDate,
    focusedDate,
    setFocusedDate,
    calendarView,
    dropdownStyle,
    panelRef,
    gridRef,
    onDayClickRef,
    handlePrevMonth,
    handleNextMonth,
    handleMonthButtonClick,
    handleYearButtonClick,
    handleMonthSelect,
    handleYearSelect,
    handleGridKeyDown,
  } = useCalendarPopup({
    open,
    isMobile,
    anchorRef,
    onClose,
    // A future minDate would otherwise open on a month with every day disabled.
    initialViewDate: selectedDate ?? (minDate && isAfter(minDate, new Date()) ? minDate : new Date()),
    minDate,
    maxDate,
    filterDate,
    baseMinWidth,
    mobileInitialFocusRef: closeButtonRef,
    mobileDialogRef,
    ownedSurfaceRefs,
  });

  // In monthYearOnly mode, selecting a month picks the 1st of that month and closes.
  const handleMonthYearOnlySelect = useCallback(
    (month: number) => {
      const selected = new Date(getYear(viewDate), month, 1);
      onSelect(selected);
      onClose();
    },
    [viewDate, onSelect, onClose]
  );

  // In yearOnly mode, selecting a year picks Jan 1 of that year and closes.
  const handleYearOnlySelect = useCallback(
    (year: number) => {
      onSelect(new Date(year, 0, 1));
      onClose();
    },
    [onSelect, onClose]
  );

  const handleDayClick = useCallback(
    (date: Date) => {
      if (isDateDisabled(date, minDate, maxDate, filterDate)) return;
      setPendingDate(date);
      setFocusedDate(date);
      // When time is not enabled, immediately confirm the selection and close.
      if (!enableTime) {
        onSelect(date);
        onClose();
      }
    },
    [minDate, maxDate, filterDate, enableTime, onSelect, onClose, setFocusedDate]
  );

  // Keep the hook's ref in sync so keyboard Enter/Space triggers handleDayClick.
  useEffect(() => {
    onDayClickRef.current = handleDayClick;
  });

  const handleDone = useCallback(() => {
    if (pendingDate) {
      onSelect(pendingDate);
    }
    // When the consumer closes asynchronously (after a save), it sets
    // closeOnConfirm=false and owns the close itself once the save resolves.
    if (closeOnConfirm) {
      onClose();
    }
  }, [pendingDate, onSelect, onClose, closeOnConfirm]);

  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  const today = startOfDay(new Date());
  const calendarDays = buildCalendarGrid(viewDate);
  const allRows = chunk(calendarDays, 7);
  // Trim the last row if it has no enabled current-month dates.
  const dayRows =
    allRows.length > 0 &&
    allRows[allRows.length - 1].every(
      (d) => !isSameMonth(d, viewDate) || isDateDisabled(d, minDate, maxDate, filterDate)
    )
      ? allRows.slice(0, -1)
      : allRows;
  const currentMonth = getMonth(viewDate);
  const currentYear = getYear(viewDate);
  const todayMonth = getMonth(today);
  const todayYear = getYear(today);
  const translatedMonth = t(`months.${MONTH_KEYS[currentMonth]}` as Parameters<typeof t>[0]);
  const gridLabel = `${translatedMonth} ${currentYear}`;

  // Build the day grid view
  const dayGridContent = (
    <>
      {/* Day-of-week header */}
      <div className={calendarWeekHeaderClasses} role="row" aria-hidden="true">
        {WEEKDAY_KEYS.map((dayKey) => (
          <abbr
            key={dayKey}
            title={t(`weekdaysFull.${dayKey}` as Parameters<typeof t>[0])}
            className={calendarWeekHeaderCellClasses}
          >
            {t(`weekdays.${dayKey}` as Parameters<typeof t>[0])}
          </abbr>
        ))}
      </div>

      {/* Day grid with roving tabindex + arrow key navigation */}
      <div
        ref={gridRef}
        className={calendarGridClasses}
        role="grid"
        aria-label={gridLabel}
        onKeyDown={handleGridKeyDown}
      >
        {dayRows.map((row, rowIdx) => (
          <div key={rowIdx} role="row" className="tw:contents">
            {row.map((date) => {
              const dateKey = date.toISOString();
              const disabled = isDateDisabled(date, minDate, maxDate, filterDate);
              const isSelected = pendingDate ? isSameDay(date, pendingDate) : false;
              const isToday = isSameDay(date, today);
              const isCurrentMonth = isSameMonth(date, viewDate);
              const isFocused = isSameDay(date, focusedDate);

              let state: 'default' | 'today' | 'selected' | 'disabled' | 'adjacent';
              if (isSelected) {
                state = 'selected';
              } else if (disabled) {
                state = 'disabled';
              } else if (!isCurrentMonth) {
                state = 'adjacent';
              } else if (isToday) {
                state = 'today';
              } else {
                state = 'default';
              }

              return (
                <button
                  key={dateKey}
                  type="button"
                  role="gridcell"
                  aria-selected={isSelected}
                  aria-disabled={disabled || undefined}
                  aria-current={isToday ? 'date' : undefined}
                  aria-label={format(date, 'EEEE, MMMM d, yyyy')}
                  tabIndex={isFocused ? 0 : -1}
                  data-cy="calendar-day"
                  onClick={disabled ? undefined : () => handleDayClick(date)}
                  className={calendarDayCellClasses({ state })}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );

  // Decide which view to render in the calendar body area
  let bodyContent: ReactNode;
  // yearOnly always shows the year grid; monthYearOnly shows the month grid instead
  // of the day grid as its default view.
  const effectiveView = yearOnly
    ? 'years'
    : monthYearOnly && calendarView === 'days'
      ? 'months'
      : calendarView;
  switch (effectiveView) {
    case 'months':
      bodyContent = (
        <MonthGrid
          selectedMonth={currentMonth}
          currentMonth={currentYear === todayYear ? todayMonth : -1}
          onSelect={monthYearOnly ? handleMonthYearOnlySelect : handleMonthSelect}
          viewYear={currentYear}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
      break;
    case 'years':
      bodyContent = (
        <YearGrid
          selectedYear={currentYear}
          currentYear={todayYear}
          onSelect={yearOnly ? handleYearOnlySelect : handleYearSelect}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
      break;
    default:
      bodyContent = dayGridContent;
  }

  // Calendar body: header + grid/months/years + live region (no footer)
  const calendarBody = (
    <>
      {/* Month/Year header with toggle buttons. yearOnly has no other view to toggle to. */}
      {!yearOnly && (
        <div className={calendarHeaderClasses}>
          <button
            type="button"
            onClick={handleMonthButtonClick}
            className={calendarSelectorButtonClasses({ active: calendarView === 'months' })}
            aria-label={t('selectMonthCurrent', { month: translatedMonth })}
            aria-expanded={calendarView === 'months'}
            data-cy="calendar-month-button"
          >
            {translatedMonth}
            <MdExpandMore className="tw:h-4 tw:w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={handleYearButtonClick}
            className={calendarSelectorButtonClasses({ active: calendarView === 'years' })}
            aria-label={t('selectYearCurrent', { year: String(currentYear) })}
            aria-expanded={calendarView === 'years'}
            data-cy="calendar-year-button"
          >
            {currentYear}
            <MdExpandMore className="tw:h-4 tw:w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Calendar body (days / months / years) */}
      {bodyContent}

      {/* Live region announces month/year changes to screen readers */}
      <div aria-live="polite" className="tw:sr-only">
        {yearOnly ? String(currentYear) : gridLabel}
      </div>
    </>
  );

  // Footer with navigation and Done button. yearOnly commits on click and pages
  // years inside YearGrid, so the month nav + Done footer has no job there.
  const calendarFooter =
    calendarView === 'days' && !yearOnly ? (
      <div className={calendarFooterClasses}>
        <div className={calendarFooterNavGroupClasses}>
          <button
            type="button"
            onClick={handlePrevMonth}
            className={calendarNavButtonClasses}
            aria-label={t('previousMonth')}
            data-cy="calendar-prev-month"
          >
            <MdChevronLeft className="tw:h-5 tw:w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={handleNextMonth}
            className={calendarNavButtonClasses}
            aria-label={t('nextMonth')}
            data-cy="calendar-next-month"
          >
            <MdChevronRight className="tw:h-5 tw:w-5" aria-hidden="true" />
          </button>
        </div>
        <div className={calendarFooterSpacerClasses} />
        <button
          type="button"
          onClick={handleDone}
          className={calendarDoneButtonClasses}
          aria-label={t('confirmSelection')}
          aria-busy={confirmLoading}
          disabled={confirmLoading}
          data-cy="calendar-done"
        >
          {confirmLoading ? (
            <CircleLoader size={CircleLoaderSize.small} color={Colors.colorBrandWhite} />
          ) : (
            <MdDone className="tw:h-5 tw:w-5" aria-hidden="true" />
          )}
        </button>
      </div>
    ) : null;

  // Inline error shown just above the Done button (async-confirm failures, PB-1712).
  const calendarErrorBanner = (
    <ErrorBanner
      message={error}
      data-cy={errorDataCy}
      className="tw:mx-[var(--spacing-base)] tw:mb-[var(--spacing-small)]"
    />
  );

  // Desktop popup height: cap to the space the popup was placed in (up to the 395px
  // frame) so the grid + time list scroll instead of clipping off-screen (PB-2407).
  // Mobile renders inside the bottom-sheet dialog, which manages its own height.
  const desktopFrameStyle = isMobile
    ? undefined
    : { height: `min(${CALENDAR_FRAME_HEIGHT}px, ${dropdownStyle.maxHeight || CALENDAR_FRAME_HEIGHT}px)` };

  // Build the complete calendar content with optional time panel
  const calendarContent = enableTime ? (
    <div
      className={calendarWithTimeContainerClasses}
      // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (computed desktop frame height)
      style={desktopFrameStyle}
      role="group"
      aria-label={title ?? t('dateTimePicker')}
    >
      <div className={calendarWithTimeBodyClasses}>
        <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0 tw:min-h-0">{calendarBody}</div>
        <TimePanel
          value={timeValue ?? null}
          onChange={(time) => onTimeChange?.(time, pendingDate)}
          interval={timeInterval}
          filterTime={filterTime}
          selectedDate={pendingDate}
        />
      </div>
      {calendarErrorBanner}
      {calendarFooter}
    </div>
  ) : (
    <div
      className={calendarContainerClasses}
      // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (computed desktop frame height)
      style={desktopFrameStyle}
      role="group"
      aria-label={title ?? t('datePicker')}
    >
      {calendarBody}
      {calendarErrorBanner}
      {calendarFooter}
    </div>
  );

  let content: ReactNode;

  if (isMobile) {
    content = (
      <>
        <div
          className={calendarMobileBackdropClasses}
          aria-hidden="true"
          onClick={handleBackdropClick}
          data-cy="calendar-backdrop"
          style={menuZIndex != null ? { zIndex: menuZIndex - 1 } : undefined}
        />
        <div
          ref={mobileDialogRef}
          className={calendarMobileDialogClasses}
          style={menuZIndex != null ? { zIndex: menuZIndex } : undefined}
          role="dialog"
          aria-modal="true"
          aria-label={title ?? t('selectDateTitle')}
          data-cy="calendar-panel"
        >
          <div className={calendarMobileHeaderClasses}>
            {title != null && <h2 className={calendarMobileTitleClasses}>{title}</h2>}
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label={tCommon('close')}
              className={calendarMobileCloseButtonClasses}
              data-cy="calendar-close"
            >
              <MdClose className="tw:h-5 tw:w-5" aria-hidden="true" />
            </button>
          </div>
          <div className={calendarMobileBodyClasses}>{calendarContent}</div>
        </div>
      </>
    );
  } else {
    content = (
      <>
        <div
          className={calendarDesktopOverlayClasses}
          aria-hidden="true"
          style={menuZIndex != null ? { zIndex: menuZIndex - 1 } : undefined}
        />
        <div
          ref={panelRef}
          className={calendarDesktopDropdownClasses}
          data-cy="calendar-panel"
          // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (computed dropdown position/width)
          style={{
            position: 'fixed',
            ...(menuZIndex != null && { zIndex: menuZIndex }),
            top: dropdownStyle.top,
            bottom: dropdownStyle.bottom,
            left: dropdownStyle.left,
            // Pin to a fixed width (not just min-width) so the calendar column can't
            // grow the panel past the width the positioning math clamped `left` for —
            // otherwise the time column overflows the right edge of the viewport (PB-2407).
            width: dropdownStyle.width,
          }}
        >
          {calendarContent}
        </div>
      </>
    );
  }

  return createPortal(content, document.body);
}
